import { useTranslation } from "react-i18next";

import type { WricefDecisionSummary } from "@/api/wricef-decisions";
import { makeExtensionTargetMeta } from "@/components/projects/diagnostic/diagnosticMeta";
import { Button } from "@/components/ui/button";
import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";

import { extensionSuggestion, suggestionApplied } from "./wricefSuggestion";

/**
 * O quadro "Sugestão do Catálogo WRICEF" na seção de extensões da Calibragem
 * (#1712, cartão 3.1).
 *
 * Mostra o que o arquiteto decidiu em escopo, por estratégia e com as horas dele,
 * e aplica nos parâmetros só no clique: Calibragem publicada não muda sozinha, e a
 * sugestão nova aparece ao abrir a tela para republicar. O que a Calibragem não
 * tem como parâmetro (em escopo sem estratégia, ou `classic`) e os pendentes com o
 * cliente ficam à parte, fora do total.
 */
export function WricefExtensionSuggestion({
  summary,
  values,
  onApply,
}: {
  summary: WricefDecisionSummary | undefined;
  /** O valor que vale em cada parâmetro: a correção da tela ou o default. */
  values: Record<string, number>;
  onApply: (values: Record<string, number>) => void;
}) {
  const { t } = useTranslation();
  const suggestion = extensionSuggestion(summary);
  if (!suggestion) return null;
  const meta = makeExtensionTargetMeta(t) as Record<string, { label: string } | undefined>;
  const label = (strategy: string) => meta[strategy]?.label ?? strategy;
  const applied = suggestionApplied(suggestion, values);

  return (
    <div
      data-testid="calibration-wricef-suggestion"
      className="flex flex-col gap-2 rounded-md border border-[#c9d8f0] bg-[#f3f7fd] p-3 text-xs"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold text-[var(--exed-ink-900)]">
          {t("Suggestion from the WRICEF Catalog")}
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          disabled={applied}
          onClick={() => onApply(suggestion.overrides)}
        >
          {applied ? t("Suggestion applied") : t("Apply suggestion")}
        </Button>
      </div>
      <ul className="flex flex-col gap-0.5">
        {suggestion.rows.map((row) => (
          <li key={row.strategy} data-testid={`wricef-suggestion-${row.strategy}`}>
            {t("{{strategy}}: {{n}} objects in scope · {{hours}} h from the architect", {
              strategy: label(row.strategy),
              n: formatCount(row.count),
              hours: formatCount(row.hours),
            })}
          </li>
        ))}
      </ul>
      <p className="font-medium">
        {t("Total suggested: {{hours}} h", { hours: formatCount(suggestion.total) })}
      </p>
      {suggestion.apart.length > 0 && (
        <p data-testid="wricef-suggestion-apart" className="text-muted-foreground">
          {t("Apart, for you to decide: {{items}}. They are not in the suggestion.", {
            items: suggestion.apart
              .map(
                (row) =>
                  `${label(row.strategy)} ${formatCount(row.count)} (${formatCount(row.hours)} h)`,
              )
              .join(", "),
          })}
        </p>
      )}
      {suggestion.missingHours > 0 && (
        <p className="text-muted-foreground">
          {tp(
            t,
            suggestion.missingHours,
            "1 in-scope object has no hours yet and is not in the total.",
            "{{n}} in-scope objects have no hours yet and are not in the total.",
            { n: formatCount(suggestion.missingHours) },
          )}
        </p>
      )}
      {suggestion.pending.count > 0 && (
        <p data-testid="wricef-suggestion-pending" className="text-muted-foreground">
          {t("{{n}} pending client validation · {{hours}} h, outside the total.", {
            n: formatCount(suggestion.pending.count),
            hours: formatCount(suggestion.pending.hours),
          })}
        </p>
      )}
      <p className="text-muted-foreground">
        {t(
          "Applying fills the quantity and the architect's hours per object below. You can still change both.",
        )}
      </p>
    </div>
  );
}
